/**
 * Créer un défi live privé — /live/prive/nouveau (migration 0650).
 *
 * Niveau, matière, pack facultatif, nombre de questions (1 à 5, toutes jouées
 * sur la feuille de brouillon — migration 0704), temps par question (1 à 3 min),
 * difficulté progressive ou constante, départ manuel ou à une heure, cover
 * (une des images prédéfinies ou une image importée, migration 0658). Les
 * questions sont tirées des SKILLCARDS par le serveur ; une fois créé, on
 * arrive dans le salon du défi (lien, QR, « Démarrer »). `?from=<code>`
 * (« Prendre ma revanche ») reprend les réglages d'un défi joué.
 *
 * Portée (0722) : programme, pack, SKILLCARDS choisies ou un exercice ; sans accès
 * payant, le serveur ne tire que dans les essais gratuits. `?exercise=<id>&card=<id>`
 * (« En faire un défi » depuis un exercice) prérègle la portée « exercice ».
 *
 * Paywall (0728) : offre de l'organisateur en tête (`PrivateLivePlan`) ; gratuit = 1 défi
 * par jour et 3 joueurs, accès complet = 50 joueurs et défis illimités.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, ListChecks, Minus, Plus, Radio } from 'lucide-react';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import { Button, Card } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useCurriculumTimeline } from '@/lib/curriculum/api';
import { LiveBackButton } from '@/features/live-quiz/live-back';
import { PrivateLivePlan } from './private-live-plan';
import { ScopeFields, cardSectionsFromPacks, lastUsedCardId } from './scope-fields';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { recentCatalogCards } from '@/features/skillcards/skillcard-catalog-modal';
import { liveCoverUrl, randomPresetPath, setPrivateLiveCover } from '@/lib/live-covers';
import { LiveCoverPicker } from '@/features/live-ui/live-cover-picker';
import { LiveCardMeta, LiveCoverCard } from '@/features/live-ui/live-cover-card';
import { levelSubjectChip, shortFormatLabel } from '@/features/live-ui/live-card-model';
import {
  PRIVATE_LIVE_MAX_QUESTIONS,
  EXERCISE_CHALLENGE_SHEET_MS,
  SHEET_DEFAULT_MS,
  SHEET_MAX_MS,
  SHEET_MIN_MS,
  SHEET_STEP_MS,
  clampSheetMs,
  privateLiveErrorMessage,
  scheduleError,
  sheetDurationLabel,
  useCreatePrivateLive,
  usePrivateLive,
  usePrivateLiveQuota,
  warmPrivateLivePreview,
  type PrivateLive,
  type PrivateLiveDifficulty,
  type PrivateLiveScope,
} from '@/lib/private-live';

/** Matières proposées pour un niveau (celles du curriculum). PUR. */
export function subjectsForLevel(level: string): { value: string; label: string }[] {
  return SUBJECTS.filter((s) => (s.levels as readonly string[]).includes(level)).map((s) => ({
    value: s.value,
    label: s.label,
  }));
}

/** Nombre de questions borné à 1..5. PUR. */
export function clampQuestionCount(n: number): number {
  return Math.min(PRIVATE_LIVE_MAX_QUESTIONS, Math.max(1, Math.round(n)));
}

/** Titre d'une revanche (« Revanche : … », 80 caractères au plus). PUR. */
export function rematchTitle(title: string | null | undefined): string {
  const base = (title ?? '').replace(/^Revanche\s*:\s*/i, '').trim();
  return (base ? `Revanche : ${base}` : 'Revanche').slice(0, 80);
}

/**
 * Réglages du formulaire repris d'un défi joué (revanche) ; un défi d'avant
 * 0704 (jusqu'à 10 questions) est ramené à 5. PUR.
 */
export function rematchForm(
  live: Pick<PrivateLive, 'level' | 'subject' | 'settings' | 'step_count' | 'title'>,
) {
  const settings = live.settings ?? {};
  return {
    level: live.level,
    subject: live.subject ?? null,
    packId: settings.pack_id ?? '',
    scope: (settings.scope ?? (settings.pack_id ? 'pack' : 'programme')) as PrivateLiveScope,
    cardIds: settings.card_ids ?? [],
    exerciseId: settings.exercise_id ?? null,
    count: clampQuestionCount(live.step_count),
    sheetMs: clampSheetMs(settings.sheet_ms ?? SHEET_DEFAULT_MS),
    progressive: settings.progressive !== false,
    difficulty: (settings.difficulty ?? 'medium') as PrivateLiveDifficulty,
    title: rematchTitle(live.title),
  };
}

const DIFFICULTIES: { value: PrivateLiveDifficulty; label: string }[] = [
  { value: 'easy', label: 'Facile' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'hard', label: 'Difficile' },
];

function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Corps envoyé à `live-private-create` pour la portée choisie : seuls les champs de
 * cette portée partent (le serveur déduit la portée de ce qu'il reçoit). PUR.
 */
export function scopeInput(opts: {
  scope: PrivateLiveScope;
  packId: string;
  cardIds: readonly string[];
  exerciseId: string | null;
}): { pack_id: string | null; card_ids: string[]; exercise_id: string | null } {
  return {
    pack_id: opts.scope === 'pack' && opts.packId ? opts.packId : null,
    card_ids: opts.scope === 'cards' ? [...opts.cardIds] : [],
    exercise_id: opts.scope === 'exercise' ? opts.exerciseId : null,
  };
}

/** Message si la portée n'est pas complète, sinon null. PUR. */
export function scopeIssue(opts: {
  scope: PrivateLiveScope;
  packId: string;
  cardIds: readonly string[];
  exerciseId: string | null;
}): string | null {
  if (opts.scope === 'pack' && !opts.packId) return 'Choisis un pack.';
  if (opts.scope === 'cards' && opts.cardIds.length === 0) return 'Choisis au moins une SKILLCARD.';
  if (opts.scope === 'exercise' && !opts.exerciseId) return 'Choisis un exercice.';
  return null;
}

export function CreatePrivateLivePage({
  fromCode,
  exercise,
}: { fromCode?: string; exercise?: { id: string; cardId: string | null } } = {}) {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  // Revanche : les réglages du défi joué (une seule lecture, pas de sondage).
  const rematch = usePrivateLive(fromCode ?? null, userId, { poll: false });
  const myLevel = useQuery({
    queryKey: ['student-profile-level', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', userId!)
        .maybeSingle();
      return (data?.level as string | null) ?? null;
    },
  });

  const [level, setLevel] = useState('3e');
  const [levelTouched, setLevelTouched] = useState(false);
  useEffect(() => {
    if (!levelTouched && myLevel.data && subjectsForLevel(myLevel.data).length > 0) {
      setLevel(myLevel.data);
    }
  }, [myLevel.data, levelTouched]);

  const subjects = useMemo(() => subjectsForLevel(level), [level]);
  const [subject, setSubject] = useState('math');
  useEffect(() => {
    if (!subjects.some((s) => s.value === subject)) setSubject(subjects[0]?.value ?? '');
  }, [subjects, subject]);

  const packs = useCurriculumTimeline(level, subject || null, !!subject);
  const [packId, setPackId] = useState('');
  // Pack repris d'une revanche : posé APRÈS le changement de niveau/matière.
  const pendingPack = useRef<string | null>(null);
  useEffect(() => {
    setPackId(pendingPack.current ?? '');
    pendingPack.current = null;
  }, [level, subject]);

  const [scope, setScope] = useState<PrivateLiveScope>(exercise ? 'exercise' : 'cards');
  const [cardIds, setCardIds] = useState<string[]>([]);
  // SKILLCARD par défaut : la dernière travaillée, parmi celles du niveau et de la matière.
  const mine = useStudentSkillcards(userId);
  const cardsTouched = useRef(false);
  const sections = useMemo(() => cardSectionsFromPacks(packs.data ?? []), [packs.data]);
  const recent = useMemo(() => {
    const ids = new Set(sections.flatMap((sec) => sec.cards.map((c) => c.id)));
    return recentCatalogCards(mine.data ?? [], ids);
  }, [mine.data, sections]);
  useEffect(() => {
    if (fromCode || exercise || cardsTouched.current || scope !== 'cards' || cardIds.length > 0)
      return;
    const id = lastUsedCardId(mine.data ?? [], sections);
    if (id) setCardIds([id]);
  }, [fromCode, exercise, scope, cardIds.length, mine.data, sections]);
  const [exerciseCardId, setExerciseCardId] = useState<string | null>(exercise?.cardId ?? null);
  const [exerciseId, setExerciseId] = useState<string | null>(exercise?.id ?? null);
  // Changer de niveau ou de matière vide les cartes choisies (elles n'y sont plus) ;
  // une revanche les repose après coup.
  const pendingCards = useRef<string[] | null>(null);
  const firstLevel = useRef(true);
  useEffect(() => {
    if (firstLevel.current) {
      firstLevel.current = false;
      return;
    }
    setCardIds(pendingCards.current ?? []);
    pendingCards.current = null;
  }, [level, subject]);

  const [count, setCount] = useState(3);
  const [sheetMs, setSheetMs] = useState(exercise ? EXERCISE_CHALLENGE_SHEET_MS : SHEET_DEFAULT_MS);
  const [progressive, setProgressive] = useState(true);
  const [difficulty, setDifficulty] = useState<PrivateLiveDifficulty>('medium');
  const [startMode, setStartMode] = useState<'manual' | 'scheduled'>('manual');
  const [at, setAt] = useState(() => toLocalInput(Date.now() + 30 * 60_000));
  const [title, setTitle] = useState('');
  const [coverPath, setCoverPath] = useState<string>(() => randomPresetPath());
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePrivateLive();
  const quotaSettings = useMemo(
    () => ({
      scope,
      level,
      subject,
      ...scopeInput({ scope, packId, cardIds, exerciseId }),
    }),
    [scope, level, subject, packId, cardIds, exerciseId],
  );
  const quota = usePrivateLiveQuota(quotaSettings, !!session);
  const blocked = quota.data ? !quota.data.can_create : false;

  // Revanche : on recopie les réglages UNE fois, quand le défi est chargé.
  const prefilled = useRef(false);
  useEffect(() => {
    const src = fromCode ? rematch.data : null;
    if (prefilled.current || !src) return;
    prefilled.current = true;
    const f = rematchForm(src);
    if (subjectsForLevel(f.level).length > 0) {
      setLevel(f.level);
      setLevelTouched(true);
      if (f.subject) setSubject(f.subject);
      pendingPack.current = f.packId || null;
    }
    setScope(f.scope === 'exercise' && !f.exerciseId ? 'programme' : f.scope);
    if (f.scope === 'cards') {
      cardsTouched.current = true;
      pendingCards.current = f.cardIds;
      setCardIds(f.cardIds);
    }
    if (f.scope === 'exercise') setExerciseId(f.exerciseId);
    setCount(f.count);
    setSheetMs(f.sheetMs);
    setProgressive(f.progressive);
    setDifficulty(f.difficulty);
    setTitle(f.title);
    if (src.cover_path) setCoverPath(src.cover_path);
  }, [fromCode, rematch.data]);

  function submit() {
    setError(null);
    const issue = scopeIssue({ scope, packId, cardIds, exerciseId });
    if (issue) {
      setError(issue);
      return;
    }
    let start: { mode: 'manual' } | { mode: 'scheduled'; at: string } = { mode: 'manual' };
    if (startMode === 'scheduled') {
      const ms = new Date(at).getTime();
      const issue = scheduleError(ms, Date.now());
      if (issue) {
        setError(issue);
        return;
      }
      start = { mode: 'scheduled', at: new Date(ms).toISOString() };
    }
    create.mutate(
      {
        level: scope === 'exercise' && exercise ? '' : level,
        subject: scope === 'exercise' && exercise ? '' : subject,
        ...scopeInput({ scope, packId, cardIds, exerciseId }),
        count,
        format: 'sheet',
        final_sheet: false,
        sheet_ms: sheetMs,
        progressive,
        difficulty,
        title: title.trim() || null,
        start,
      },
      {
        onSuccess: async (r) => {
          // La cover se pose après la création ; un échec garde le preset
          // tiré du défi (jamais bloquant).
          await setPrivateLiveCover(r.challenge_id, coverPath).catch(() => undefined);
          warmPrivateLivePreview(r.join_code);
          window.location.href = `/defi-live/${r.join_code}`;
        },
        onError: (e) => setError(privateLiveErrorMessage(e.message)),
      },
    );
  }

  return (
    <div className="pb-safe native:pt-[calc(env(safe-area-inset-top)+1rem)] mx-auto w-full max-w-lg px-4 py-6 sm:py-8">
      <LiveBackButton className="-ml-1 mb-2" />
      <p className="text-primary-600 text-xs font-semibold uppercase tracking-wide">
        {fromCode ? 'Revanche' : 'Live School'}
      </p>
      <h1 className="text-ink-900 mb-1 text-xl font-bold">
        {fromCode ? 'Prendre ma revanche' : 'Créer un défi privé'}
      </h1>
      <p className="text-ink-600 mb-5 text-sm">
        {fromCode
          ? 'Les réglages du défi sont repris : change ce que tu veux, puis relance. Les questions seront nouvelles.'
          : 'Choisis le niveau et les questions, partage le lien, et lance le défi quand tout le monde est là. Il n’apparaît pas dans les lives publics.'}
      </p>

      {quota.data && <PrivateLivePlan quota={quota.data} />}

      {/* Aperçu de la carte du défi, telle qu'elle apparaîtra dans le Feed. */}
      <LiveCoverCard
        size="md"
        badge="private"
        coverUrl={liveCoverUrl({ path: coverPath })}
        chip={levelSubjectChip(level, subject)}
        title={title.trim() || 'Défi privé'}
        className="mb-4"
        testId="private-live-preview"
      >
        <LiveCardMeta
          className="mt-2"
          items={[
            {
              icon: ListChecks,
              text: shortFormatLabel({ format: 'sheet' }, count),
            },
            {
              icon: Clock,
              text: startMode === 'manual' ? 'Tu lances quand tu veux' : 'Départ programmé',
            },
          ]}
        />
      </LiveCoverCard>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Card className="p-4 sm:p-5">
          <LiveCoverPicker value={coverPath} onChange={setCoverPath} userId={userId} />
        </Card>

        <Card className="space-y-4 p-4 sm:p-5">
          {!(exercise && scope === 'exercise') && (
            <>
              <Field label="Niveau" htmlFor="pl-level">
                <select
                  id="pl-level"
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value);
                    setLevelTouched(true);
                  }}
                  className={SELECT}
                >
                  {SCHOOL_LEVELS.filter((l) => subjectsForLevel(l.value).length > 0).map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Matière" htmlFor="pl-subject">
                <select
                  id="pl-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={SELECT}
                >
                  {subjects.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          <ScopeFields
            packs={packs.data ?? []}
            scope={scope}
            onScope={setScope}
            packId={packId}
            onPackId={setPackId}
            cardIds={cardIds}
            onCardIds={(ids) => {
              cardsTouched.current = true;
              setCardIds(ids);
            }}
            recent={recent}
            exerciseCardId={exerciseCardId}
            exerciseId={exerciseId}
            onExercise={(cardId, exId, questions) => {
              setExerciseCardId(cardId);
              setExerciseId(exId);
              if (questions) setCount(clampQuestionCount(questions));
            }}
            userId={userId}
          />
        </Card>

        <Card className="space-y-4 p-4 sm:p-5">
          <div>
            <p className="text-ink-700 mb-2 text-sm font-semibold">Nombre de questions</p>
            <div className="flex items-center gap-3">
              <StepButton
                label="Une question de moins"
                disabled={count <= 1}
                onClick={() => setCount((c) => clampQuestionCount(c - 1))}
              >
                <Minus className="h-4 w-4" />
              </StepButton>
              <span
                className="font-display text-ink-900 w-10 text-center text-2xl font-black tabular-nums"
                aria-live="polite"
              >
                {count}
              </span>
              <StepButton
                label="Une question de plus"
                disabled={count >= PRIVATE_LIVE_MAX_QUESTIONS}
                onClick={() => setCount((c) => clampQuestionCount(c + 1))}
              >
                <Plus className="h-4 w-4" />
              </StepButton>
              <span className="text-ink-500 text-xs">{PRIVATE_LIVE_MAX_QUESTIONS} au plus</span>
            </div>
            <p className="text-ink-500 mt-1.5 text-xs">
              Chaque question se résout sur la feuille de brouillon : chaque ligne juste rapporte
              des HORA.
            </p>
          </div>

          <div>
            <p className="text-ink-700 mb-2 text-sm font-semibold">Temps par question</p>
            <div className="flex items-center gap-3">
              <StepButton
                label="30 secondes de moins"
                disabled={sheetMs <= SHEET_MIN_MS}
                onClick={() => setSheetMs((v) => clampSheetMs(v - SHEET_STEP_MS))}
              >
                <Minus className="h-4 w-4" />
              </StepButton>
              <span
                className="font-display text-ink-900 min-w-24 text-center text-lg font-black tabular-nums"
                aria-live="polite"
              >
                {sheetDurationLabel(sheetMs)}
              </span>
              <StepButton
                label="30 secondes de plus"
                disabled={sheetMs >= SHEET_MAX_MS}
                onClick={() => setSheetMs((v) => clampSheetMs(v + SHEET_STEP_MS))}
              >
                <Plus className="h-4 w-4" />
              </StepButton>
              <span className="text-ink-500 text-xs">1 à 3 min</span>
            </div>
          </div>

          <div>
            <p className="text-ink-700 mb-2 text-sm font-semibold">Difficulté</p>
            <Segmented
              value={progressive ? 'progressive' : 'constant'}
              onChange={(v) => setProgressive(v === 'progressive')}
              options={[
                { value: 'progressive', label: 'Progressive' },
                { value: 'constant', label: 'Constante' },
              ]}
            />
            {!progressive && (
              <div className="mt-2">
                <Segmented value={difficulty} onChange={setDifficulty} options={DIFFICULTIES} />
              </div>
            )}
          </div>
        </Card>

        <Card className="space-y-3 p-4 sm:p-5">
          <p className="text-ink-700 text-sm font-semibold">Départ</p>
          <Segmented
            value={startMode}
            onChange={setStartMode}
            options={[
              { value: 'manual', label: 'Je lance moi-même' },
              { value: 'scheduled', label: 'À une heure' },
            ]}
          />
          {startMode === 'scheduled' && (
            <input
              type="datetime-local"
              aria-label="Heure de départ"
              value={at}
              onChange={(e) => setAt(e.target.value)}
              className={SELECT}
            />
          )}
          <Field label="Titre (facultatif)" htmlFor="pl-title">
            <input
              id="pl-title"
              value={title}
              maxLength={80}
              placeholder="Défi du vendredi"
              onChange={(e) => setTitle(e.target.value)}
              className={SELECT}
            />
          </Field>
        </Card>

        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}
        <Button
          type="submit"
          variant="gold"
          fullWidth
          className="min-h-12"
          loading={create.isPending}
          disabled={!subject || blocked}
        >
          <Radio className="h-4 w-4" aria-hidden />
          Créer le défi
        </Button>
      </form>
    </div>
  );
}

const SELECT =
  'border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3 text-sm focus:outline-none';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-ink-700 block text-sm font-semibold">
        {label}
      </label>
      {children}
    </div>
  );
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="border-ink-200 text-ink-700 flex h-11 w-11 items-center justify-center rounded-full border active:scale-[0.98] disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div
      className="bg-ink-100 grid auto-cols-fr grid-flow-col gap-1 rounded-xl p-1"
      role="radiogroup"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`min-h-10 rounded-lg px-2 text-sm font-semibold transition-colors active:scale-[0.98] ${
            value === o.value
              ? 'text-primary-700 ocean:bg-primary-500 ocean:text-[#ffffff] bg-[#ffffff] shadow-sm'
              : 'text-ink-600'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
